import React, { useState } from 'react';
import { usePageTitle } from '../../hooks/usePageTitle';
import {
  Menu,
  X,
  ArrowRight,
  ShoppingCart,
  ClipboardList,
  Package,
  Users,
  BarChart3,
  DollarSign,
  CheckCircle2,
  ChevronRight,
  AlertTriangle,
  Sparkles,
  Heart,
  MessageCircle,
  Gift,
  Star,
  UserCheck,
  Lock,
} from 'lucide-react';

const PROBLEMAS = [
  {
    icon: ClipboardList,
    titulo: 'Encomendas perdidas no caderno',
    descricao: 'Anotou no papel, esqueceu a data, confundiu o sabor. A cliente liga cobrando e você nem lembra do pedido.',
  },
  {
    icon: Package,
    titulo: 'Estoque sempre no escuro',
    descricao: 'Acabou a manteiga no meio do bolo. Comprou farinha demais. Não sabe o que tem, o que falta, o que venceu.',
  },
  {
    icon: DollarSign,
    titulo: 'Não saber o lucro real',
    descricao: 'Vendeu bastante, mas no fim do mês o dinheiro sumiu. Quanto foi custo? Quanto foi lucro? Ninguém sabe.',
  },
  {
    icon: AlertTriangle,
    titulo: 'Caixa misturado com conta pessoal',
    descricao: 'Pagou o fornecedor com o Pix pessoal, tirou dinheiro do caixa pra almoçar. No fim do dia, os números não fecham.',
  },
  {
    icon: BarChart3,
    titulo: 'Fechamento de caixa confuso',
    descricao: 'Aquela sensação de que vendeu bem, mas quando vai somar... não bate. E você não sabe onde foi o erro.',
  },
];

const FUNCIONALIDADES = [
  {
    icon: ShoppingCart,
    titulo: 'Ponto de Venda simples',
    descricao: 'Registre vendas de balcão em poucos cliques. Sem complicação.',
  },
  {
    icon: ClipboardList,
    titulo: 'Controle de encomendas',
    descricao: 'Cadastre, acompanhe o status e nunca mais esqueça uma entrega.',
  },
  {
    icon: Package,
    titulo: 'Gestão de estoque',
    descricao: 'Saiba o que tem, o que está acabando e receba alertas automáticos.',
  },
  {
    icon: Users,
    titulo: 'Cadastro de clientes',
    descricao: 'Histórico de pedidos e encomendas de cada cliente em um só lugar.',
  },
  {
    icon: DollarSign,
    titulo: 'Fechamento de caixa',
    descricao: 'Resumo claro do dia: o que entrou, o que saiu, quanto sobrou.',
  },
  {
    icon: BarChart3,
    titulo: 'Relatórios que fazem sentido',
    descricao: 'Vendas por dia, produtos mais vendidos, ticket médio. Sem planilha.',
  },
];

const LandingHeader: React.FC = () => {
  const [menuAberto, setMenuAberto] = useState(false);

  const scrollTo = (id: string) => {
    setMenuAberto(false);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <header className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-sm border-b border-neutral-200 shadow-sm">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Gestta" className="h-8 brightness-0" />
            <span className="font-display font-bold text-xl text-neutral-900">Gestta</span>
          </div>

          <nav className="hidden lg:flex items-center gap-8">
            <button onClick={() => scrollTo('problemas')} className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">
              O Problema
            </button>
            <button onClick={() => scrollTo('solucao')} className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">
              A Solução
            </button>
            <button onClick={() => scrollTo('historia')} className="text-sm font-medium text-neutral-600 hover:text-neutral-900 transition-colors">
              Nossa História
            </button>
            <button
              onClick={() => scrollTo('grupo-fundador')}
              className="bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-6 py-2.5 rounded-lg text-sm transition-all duration-200 shadow-md hover:shadow-lg"
            >
              Quero Participar
            </button>
          </nav>

          <button
            onClick={() => setMenuAberto(!menuAberto)}
            className="lg:hidden p-2 text-neutral-600 hover:text-neutral-900"
            aria-label={menuAberto ? 'Fechar menu' : 'Abrir menu'}
          >
            {menuAberto ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {menuAberto && (
          <div className="lg:hidden border-t border-neutral-100 py-4 space-y-3 animate-fadeIn">
            <button onClick={() => scrollTo('problemas')} className="block w-full text-left px-2 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900">
              O Problema
            </button>
            <button onClick={() => scrollTo('solucao')} className="block w-full text-left px-2 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900">
              A Solução
            </button>
            <button onClick={() => scrollTo('historia')} className="block w-full text-left px-2 py-2 text-sm font-medium text-neutral-600 hover:text-neutral-900">
              Nossa História
            </button>
            <button
              onClick={() => scrollTo('grupo-fundador')}
              className="block w-full text-center bg-neutral-900 text-white font-semibold px-6 py-2.5 rounded-lg text-sm"
            >
              Quero Participar
            </button>
          </div>
        )}
      </div>
    </header>
  );
};

const HeroSection: React.FC = () => (
  <section className="relative pt-16 bg-white overflow-hidden">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 text-center">
      <span className="inline-block bg-success-50 text-success-700 border border-success-200 rounded-full px-4 py-1.5 text-sm font-medium mb-8">
        Acesso antecipado gratuito &mdash; vagas limitadas
      </span>

      <h1 className="font-display text-4xl md:text-5xl lg:text-[3.5rem] font-bold text-neutral-900 leading-tight">
        Sua confeitaria organizada.<br />
        <span className="text-neutral-500">Sem planilha. Sem caderno. Sem bagunça.</span>
      </h1>

      <p className="text-lg md:text-xl text-neutral-600 mt-8 max-w-2xl mx-auto leading-relaxed">
        O Gestta é um sistema de gestão feito por quem vive a rotina de uma confeitaria.
        Simples de usar, pensado para quem vende no balcão e trabalha com encomendas.
      </p>

      <div className="flex flex-col sm:flex-row gap-4 mt-10 justify-center">
        <button
          onClick={() => document.getElementById('grupo-fundador')?.scrollIntoView({ behavior: 'smooth' })}
          className="inline-flex items-center justify-center gap-2 bg-neutral-900 text-white hover:bg-neutral-800 font-bold px-8 py-4 rounded-xl text-lg shadow-lg transition-all duration-200 hover:shadow-xl hover:scale-[1.02]"
        >
          Quero Participar do Grupo Fundador
          <ArrowRight size={20} />
        </button>
      </div>

      <p className="text-neutral-400 text-sm mt-4">
        100% gratuito durante o período de validação
      </p>
    </div>

    <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-neutral-50 to-transparent" />
  </section>
);

const ProblemasSection: React.FC = () => (
  <section id="problemas" className="py-20 lg:py-28 bg-neutral-50">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-neutral-900">
          Se você se identifica com isso,<br />o Gestta foi feito pra você.
        </h2>
        <p className="text-neutral-500 text-lg mt-4 max-w-2xl mx-auto">
          Esses problemas parecem pequenos, mas juntos consomem tempo, dinheiro e energia todos os dias.
        </p>
      </div>

      <div className="space-y-4">
        {PROBLEMAS.map((problema) => {
          const Icon = problema.icon;
          return (
            <div
              key={problema.titulo}
              className="bg-white rounded-xl border border-neutral-200 p-6 flex items-start gap-4 hover:border-neutral-300 transition-colors"
            >
              <div className="w-10 h-10 bg-danger-50 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                <Icon size={20} className="text-danger-500" />
              </div>
              <div>
                <h3 className="font-semibold text-neutral-900 text-lg">{problema.titulo}</h3>
                <p className="text-neutral-500 mt-1 leading-relaxed">{problema.descricao}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-12 text-center">
        <p className="text-neutral-600 text-lg font-medium">
          A maioria das confeitarias tenta resolver isso com caderno, planilha ou memória.
        </p>
        <p className="text-neutral-900 text-xl font-display font-bold mt-2">
          Funciona por um tempo. Até parar de funcionar.
        </p>
      </div>
    </div>
  </section>
);

const SolucaoSection: React.FC = () => (
  <section id="solucao" className="py-20 lg:py-28 bg-white">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="max-w-3xl mx-auto text-center mb-16">
        <span className="inline-block bg-primary-50 text-primary-700 border border-primary-200 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
          A solução
        </span>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-neutral-900">
          Um sistema que entende a rotina de quem faz doce
        </h2>
        <p className="text-neutral-500 text-lg mt-4 leading-relaxed">
          O Gestta não é um ERP genérico adaptado. Ele foi construído de dentro pra fora,
          a partir dos problemas reais de uma confeitaria que vende no balcão e entrega encomendas.
        </p>
        <p className="text-neutral-700 text-lg mt-4 font-medium">
          O foco é simples: <strong>organização e clareza</strong>. Saber o que vendeu, o que tem que entregar,
          o que tem no estoque e quanto sobrou no fim do dia.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {FUNCIONALIDADES.map((func) => {
          const Icon = func.icon;
          return (
            <div
              key={func.titulo}
              className="bg-neutral-50 rounded-xl border border-neutral-100 p-6 hover:bg-white hover:shadow-md hover:border-neutral-200 transition-all duration-300"
            >
              <div className="w-11 h-11 bg-white rounded-lg flex items-center justify-center mb-4 border border-neutral-200">
                <Icon size={22} className="text-neutral-700" />
              </div>
              <h3 className="font-semibold text-neutral-900 mb-1">{func.titulo}</h3>
              <p className="text-neutral-500 text-sm leading-relaxed">{func.descricao}</p>
            </div>
          );
        })}
      </div>

      <div className="mt-12 bg-neutral-50 rounded-2xl border border-neutral-200 p-8 text-center">
        <Sparkles size={28} className="text-warning-500 mx-auto mb-4" />
        <p className="text-neutral-900 text-lg font-display font-bold">
          Simples na superfície. Inteligente por dentro.
        </p>
        <p className="text-neutral-500 mt-2 max-w-lg mx-auto">
          Você não precisa ser expert em tecnologia. Se sabe usar o celular, sabe usar o Gestta.
          Ele roda no navegador, no computador ou no celular.
        </p>
      </div>
    </div>
  </section>
);

const HistoriaSection: React.FC = () => (
  <section id="historia" className="py-20 lg:py-28 bg-neutral-50">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-12">
        <span className="inline-block bg-warning-50 text-warning-700 border border-warning-200 rounded-full px-4 py-1.5 text-sm font-medium mb-6">
          Como tudo começou
        </span>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-neutral-900">
          O Gestta nasceu dentro de uma confeitaria
        </h2>
      </div>

      <div className="bg-white rounded-2xl border border-neutral-200 p-8 md:p-10 space-y-6 text-neutral-600 leading-relaxed">
        <p className="text-lg">
          Tudo começou quando acompanhamos de perto a rotina de uma confeitaria real.
          Encomendas anotadas em papel, estoque controlado de cabeça, caixa fechado no chute.
        </p>
        <p>
          A dona fazia bolos incríveis, mas vivia apagando incêndio: esquecia entrega,
          comprava ingrediente repetido, e no fim do mês não sabia se tinha dado lucro ou prejuízo.
        </p>
        <p>
          Procuramos sistemas no mercado. Todos eram genéricos demais, complexos demais,
          ou caros demais para uma confeitaria pequena.
        </p>
        <p className="text-neutral-900 font-medium text-lg">
          Então decidimos criar o que não existia: um sistema simples, pensado especificamente
          para quem trabalha com confeitaria, doceria e cafeteria.
        </p>
        <p>
          Não é um sistema que tenta fazer tudo. É um sistema que faz o essencial muito bem:
          organizar vendas, encomendas, estoque e caixa. Sem complicação.
        </p>

        <div className="border-t border-neutral-100 pt-6 flex items-center gap-4">
          <div className="w-12 h-12 bg-neutral-100 rounded-full flex items-center justify-center">
            <Heart size={20} className="text-danger-400" />
          </div>
          <p className="text-neutral-500 text-sm italic">
            "A gente não criou o Gestta pra ser o maior sistema do mercado.
            A gente criou pra ser o mais útil pra quem faz doce."
          </p>
        </div>
      </div>
    </div>
  </section>
);

const GrupoFundadorSection: React.FC = () => (
  <section id="grupo-fundador" className="py-20 lg:py-28 bg-white">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-16">
        <span className="inline-block bg-neutral-900 text-white rounded-full px-5 py-2 text-sm font-semibold mb-6">
          Grupo Fundador
        </span>
        <h2 className="font-display text-3xl md:text-4xl font-bold text-neutral-900">
          Estamos abrindo vagas para confeitarias<br />que querem testar o Gestta antes de todo mundo
        </h2>
        <p className="text-neutral-500 text-lg mt-4 max-w-2xl mx-auto">
          Antes de lançar publicamente, queremos que confeitarias reais usem o sistema no dia a dia
          e nos ajudem a torná-lo ainda melhor.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* O que você ganha */}
        <div className="bg-success-50 rounded-2xl border border-success-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <Gift size={24} className="text-success-600" />
            <h3 className="font-display font-bold text-neutral-900 text-xl">O que você ganha</h3>
          </div>
          <ul className="space-y-4">
            {[
              'Acesso completo e gratuito ao sistema durante toda a fase de validação',
              'Canal direto com a equipe de desenvolvimento para sugestões e dúvidas',
              'Prioridade nas novas funcionalidades (você pede, a gente constrói)',
              'Condição especial permanente quando o sistema for lançado oficialmente',
              'Participação ativa na construção de um produto feito pra você',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <CheckCircle2 size={18} className="text-success-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700 text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* O que esperamos */}
        <div className="bg-primary-50 rounded-2xl border border-primary-200 p-8">
          <div className="flex items-center gap-3 mb-6">
            <MessageCircle size={24} className="text-primary-600" />
            <h3 className="font-display font-bold text-neutral-900 text-xl">O que esperamos de você</h3>
          </div>
          <ul className="space-y-4">
            {[
              'Usar o Gestta no dia a dia da sua confeitaria (não precisa ser todos os dias)',
              'Compartilhar feedback sincero sobre o que funciona e o que pode melhorar',
              'Relatar problemas que encontrar para que possamos corrigir rapidamente',
              'Sugerir funcionalidades que fariam diferença na sua rotina',
              'Ter paciência com ajustes (o sistema está em evolução constante)',
            ].map((item, i) => (
              <li key={i} className="flex items-start gap-3">
                <UserCheck size={18} className="text-primary-600 flex-shrink-0 mt-0.5" />
                <span className="text-neutral-700 text-sm leading-relaxed">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      <div className="mt-10 bg-warning-50 rounded-xl border border-warning-200 p-6 flex items-start gap-4">
        <Lock size={20} className="text-warning-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-neutral-900 font-semibold">Por que vagas limitadas?</p>
          <p className="text-neutral-600 text-sm mt-1 leading-relaxed">
            Queremos acompanhar de perto cada confeitaria que entrar no grupo. Isso só funciona se o
            número for pequeno o suficiente para que a gente consiga ouvir, responder e ajustar com agilidade.
            Não é marketing. É compromisso com qualidade.
          </p>
        </div>
      </div>
    </div>
  </section>
);

const CTASection: React.FC = () => (
  <section className="py-20 lg:py-28 bg-neutral-900">
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <Star size={32} className="text-warning-400 mx-auto mb-6" />

      <h2 className="font-display text-3xl md:text-4xl font-bold text-white">
        Sua confeitaria merece sair do caderno
      </h2>
      <p className="text-neutral-400 text-lg mt-4 max-w-2xl mx-auto leading-relaxed">
        Se você trabalha com confeitaria, doceria ou cafeteria e quer mais organização no dia a dia,
        o Gestta foi feito pra você. E agora você pode testar antes de todo mundo, de graça.
      </p>

      <div className="mt-10 space-y-4">
        <a
          href="https://ig.me/m/gestta.pro"
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center justify-center gap-2 bg-white text-neutral-900 hover:bg-neutral-100 font-bold px-10 py-4 rounded-xl text-lg shadow-xl transition-all duration-200 hover:shadow-2xl hover:scale-[1.02]"
        >
          Quero Participar do Grupo Fundador
          <ArrowRight size={20} />
        </a>
        <p className="text-neutral-500 text-sm">
          Você será direcionado para o Instagram. Sem formulário, sem burocracia.
        </p>
      </div>

      <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 text-neutral-500 text-sm">
        <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-success-400" /> 100% gratuito</span>
        <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-success-400" /> Sem cartão de crédito</span>
        <span className="flex items-center gap-2"><CheckCircle2 size={16} className="text-success-400" /> Suporte direto com a equipe</span>
      </div>
    </div>
  </section>
);

const LandingFooter: React.FC = () => (
  <footer className="bg-neutral-950 py-12">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Gestta" className="h-8 brightness-0 invert" />
          <span className="font-display font-bold text-lg text-white">Gestta</span>
        </div>

        <p className="text-neutral-500 text-sm text-center max-w-md">
          Gestão simples para confeitarias, docerias e cafeterias.
          Feito por quem entende a sua rotina.
        </p>

        <div className="flex items-center gap-6">
          <a
            href="https://ig.me/m/gestta.pro"
            target="_blank"
            rel="noopener noreferrer"
            className="text-neutral-400 hover:text-white text-sm font-medium transition-colors flex items-center gap-1"
          >
            Fale Conosco <ChevronRight size={14} />
          </a>
        </div>
      </div>

      <div className="border-t border-neutral-800 mt-8 pt-8 text-center">
        <p className="text-neutral-600 text-xs">
          &copy; {new Date().getFullYear()} Gestta. Todos os direitos reservados. &middot; gestta-pro.com.br
        </p>
      </div>
    </div>
  </footer>
);

const SaibaMais: React.FC = () => {
  usePageTitle('Gestta - Gestão para Confeitarias');

  return (
    <div className="min-h-screen" style={{ scrollBehavior: 'smooth' }}>
      <LandingHeader />
      <main>
        <HeroSection />
        <ProblemasSection />
        <SolucaoSection />
        <HistoriaSection />
        <GrupoFundadorSection />
        <CTASection />
      </main>
      <LandingFooter />
    </div>
  );
};

export default SaibaMais;
